import type { Block, Document, Fragment, Value } from '@gitbook/slate';

import * as prompt from '../prompt';
import {
    BLOCK_CODE_LINE,
    FRAGMENT_ANNOTATION_BODY,
    FRAGMENT_CAPTION,
    INLINE_ANNOTATION,
    INLINE_BUTTON,
    INLINE_EXPRESSION,
    INLINE_IMAGE,
    type INLINE_TYPES,
    LEAF_RICH_BLOCKS,
} from '../types';

/**
 * Check if an inline type can be inserted in the one of following contexts:-
 * - block node of a document
 * - nearest parent fragment of a block node
 */
export function canInsertInlineIn(
    context:
        | { block: Block; document: Document; nearestFragment?: undefined }
        | { block: Block; document?: undefined; nearestFragment: Fragment },
    type: INLINE_TYPES
): boolean {
    const parentToConsiders: Array<Block | Fragment> = [context.block];

    if (context.nearestFragment) {
        parentToConsiders.push(context.nearestFragment);
    } else {
        const { block, document } = context;
        const fragment = document.getClosestFragment(block.key);
        if (fragment) {
            parentToConsiders.push(fragment);
        }
    }

    return parentToConsiders.every((parentToConsider) => {
        switch (parentToConsider.object) {
            case 'block':
                if (LEAF_RICH_BLOCKS.includes(parentToConsider.type)) {
                    return true;
                }

                if (parentToConsider.type === BLOCK_CODE_LINE) {
                    if (
                        type === INLINE_ANNOTATION &&
                        context.document &&
                        prompt.hasPromptAncestor(
                            context.document.getAncestors(parentToConsider.key)
                        )
                    ) {
                        return false;
                    }

                    return type === INLINE_ANNOTATION || type === INLINE_EXPRESSION;
                }

                return false;
            case 'fragment':
                /**
                 * We don't want to allow inserting an inline-image, button or annotation in caption
                 * as it's not great UX to do that
                 */
                if (parentToConsider.fragmentKey === FRAGMENT_CAPTION) {
                    return ![INLINE_IMAGE, INLINE_ANNOTATION, INLINE_BUTTON].includes(type);
                }

                /**
                 * We don't want to allow nested annotation
                 */
                if (
                    type === INLINE_ANNOTATION &&
                    parentToConsider.type === FRAGMENT_ANNOTATION_BODY
                ) {
                    return false;
                }

                return true;
            default:
                return false;
        }
    });
}

/**
 * Check if an inline type can be inserted at the current selection.
 */
export function canInsertInlineAt(value: Value, type: INLINE_TYPES): boolean {
    const { document, startBlock } = value;

    if (!startBlock) {
        return false;
    }

    return canInsertInlineIn(
        {
            block: startBlock,
            document,
        },
        type
    );
}
